import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Ban } from 'lucide-react';
import {
  Badge,
  type BadgeProps,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  InfoTip,
  PageBody,
  SectionLabel,
  TableActionsMenu,
} from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { SkillBroadcastForm } from '@/components/admin/skill-broadcasts/SkillBroadcastForm.tsx';
import {
  cancelSkillBroadcast,
  createSkillBroadcast,
  listSkillBroadcasts,
  type SkillBroadcast,
  type SkillBroadcastStatus,
} from '@/lib/skillBroadcasts.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import { formatDate, formatDateOnly } from '@/utils/dateUtils.ts';

export const Route = createFileRoute('/_app/admin/skill-broadcasts')({
  staticData: { orgScope: 'none' },
  component: SkillBroadcastsPage,
});

/**
 * Skill broadcasts: plan one, and see what has gone out. One page, because the
 * history is the only confirmation a plan gets that it is queued, and of how
 * many people it reached.
 */
export function SkillBroadcastsPage() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [createError, setCreateError] = useState('');
  /* Bumped on every successful plan; remounting the form is what empties it. */
  const [formGeneration, setFormGeneration] = useState(0);
  const [cancelling, setCancelling] = useState<SkillBroadcast | null>(null);

  const broadcasts = useQuery({
    queryKey: ['skillBroadcasts', page],
    queryFn: () => listSkillBroadcasts(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const rows = useMemo(() => broadcasts.data?.data ?? [], [broadcasts.data]);
  const total = broadcasts.data?.meta.totalItems ?? 0;

  const plan = useMutation({
    mutationFn: createSkillBroadcast,
    onSuccess: async () => {
      setCreateError('');
      setFormGeneration((generation) => generation + 1);
      setPage(1);
      await queryClient.invalidateQueries({ queryKey: ['skillBroadcasts'] });
    },
    onError: (failure) =>
      setCreateError(
        apiErrorMessage(
          failure,
          failure instanceof ApiError && failure.status === 422
            ? 'This broadcast was refused. Check the skill is visible to every chosen cohort, and that the date is today or later.'
            : 'Failed to plan the broadcast',
        ),
      ),
  });

  return (
    <>
      <PageHeader title='Skill broadcasts' />
      <PageBody className='flex flex-col gap-7'>
        <p className='text-sm text-ink-muted'>
          Plan a skill push and send it to every active user in the chosen cohorts on a
          chosen day.
        </p>

        <ReadingColumn>
          <SkillBroadcastForm
            key={formGeneration}
            saving={plan.isPending}
            error={createError}
            onSubmit={(request) => {
              setCreateError('');
              plan.mutate(request);
            }}
          />
        </ReadingColumn>

        <section className='flex flex-col gap-3'>
          <SectionLabel as='h2'>Planned &amp; sent</SectionLabel>
          <DataTable<SkillBroadcast>
            label='Skill broadcasts'
            columns={skillBroadcastColumns}
            rows={rows}
            rowKey={(broadcast) => broadcast.id}
            loading={broadcasts.isFetching}
            pagination={{ page, pageSize: PAGE_SIZE, total }}
            onPageChange={setPage}
            emptyState={
              <EmptyState
                title='No skill broadcasts yet'
                description='Plan one above to send a skill to a cohort.'
              />
            }
            actions={(broadcast) => (
              <TableActionsMenu
                label={`Actions for ${broadcast.skill_name}`}
                items={
                  broadcast.status === 'pending'
                    ? [
                        {
                          label: 'Cancel',
                          icon: <Ban aria-hidden='true' />,
                          tone: 'danger' as const,
                          onSelect: () => setCancelling(broadcast),
                        },
                      ]
                    : []
                }
              />
            )}
          />
        </section>
      </PageBody>

      <CancelBroadcastDialog broadcast={cancelling} onClose={() => setCancelling(null)} />
    </>
  );
}

interface CancelBroadcastDialogProps {
  /** The broadcast to cancel; null keeps the dialog closed. */
  broadcast: SkillBroadcast | null;
  onClose: () => void;
}

function CancelBroadcastDialog({ broadcast, onClose }: CancelBroadcastDialogProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const cancel = useMutation({
    mutationFn: (pending: SkillBroadcast) => cancelSkillBroadcast(pending.id),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['skillBroadcasts'] });
    },
    onError: (failure) =>
      setError(
        apiErrorMessage(
          failure,
          failure instanceof ApiError && failure.status === 409
            ? 'Only pending broadcasts can be cancelled'
            : 'Failed to cancel the broadcast',
        ),
      ),
  });

  return (
    <ConfirmDialog
      open={broadcast !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setError('');
        }
      }}
      title='Cancel this broadcast?'
      description={
        broadcast
          ? `"${broadcast.skill_name}" will not be sent. This cannot be undone.`
          : undefined
      }
      confirmLabel='Cancel broadcast'
      cancelLabel='Keep it'
      variant='danger'
      error={error}
      onConfirm={async () => {
        if (!broadcast) return;
        setError('');
        await cancel.mutateAsync(broadcast).catch(() => undefined);
      }}
    />
  );
}

function statusTone(status: SkillBroadcastStatus): NonNullable<BadgeProps['tone']> {
  if (status === 'dispatched') return 'success';
  if (status === 'pending') return 'info';
  return 'quiet';
}

/* A broadcast without a send instant goes out on its scheduled day. */
function sendAtLabel(broadcast: SkillBroadcast): string {
  if (!broadcast.send_at) return formatDateOnly(broadcast.scheduled_for);

  return new Date(broadcast.send_at).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const skillBroadcastColumns: DataTableColumn<SkillBroadcast>[] = [
  {
    key: 'skill_name',
    header: 'Skill',
    render: (broadcast) => (
      <span className='font-semibold text-ink'>{broadcast.skill_name}</span>
    ),
  },
  {
    key: 'send_at',
    header: 'Send at',
    width: 'w-40',
    render: (broadcast) => (
      <span className='whitespace-nowrap'>{sendAtLabel(broadcast)}</span>
    ),
  },
  {
    key: 'cohorts',
    header: 'Cohorts',
    width: 'w-32',
    render: (broadcast) => (
      <span className='inline-flex items-center gap-1 whitespace-nowrap'>
        {broadcast.cohorts.length} cohort{broadcast.cohorts.length === 1 ? '' : 's'}
        {broadcast.cohorts.length > 0 && (
          <InfoTip
            label='Which cohorts'
            content={
              <span className='flex flex-col gap-0.5'>
                {broadcast.cohorts.map((cohort) => (
                  <span key={cohort.id}>
                    {cohort.organisation_name} — {cohort.title}
                  </span>
                ))}
              </span>
            }
          />
        )}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 'w-32',
    render: (broadcast) => (
      <Badge tone={statusTone(broadcast.status)}>{broadcast.status}</Badge>
    ),
  },
  {
    key: 'recipient_count',
    header: 'Queued',
    width: 'w-20',
    render: (broadcast) =>
      broadcast.recipient_count === null ? '—' : String(broadcast.recipient_count),
  },
  {
    key: 'created_at',
    header: 'Created',
    width: 'w-32',
    render: (broadcast) => (
      <span className='whitespace-nowrap text-ink-secondary'>
        {formatDate(broadcast.created_at)}
      </span>
    ),
  },
];
